import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OAuthService } from 'src/app/common/user_and_auth/oauth.service';
import { isOAuthProviderId } from 'src/app/common/user_and_auth/oauth-providers';

/**
 * Landing page for OAuth provider redirects (/auth/callback/:provider).
 * Reads `code` + `state` from the query string and hands them to OAuthService.
 * Navigates to the originally-requested returnUrl on success, or shows an
 * inline error with a link back to /login on failure.
 */
@Component({
  templateUrl: './auth-callback.component.html',
  styleUrls: ['./auth-callback.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthCallbackComponent implements OnInit {
  readonly message = signal('Completing sign-in…');
  readonly errored = signal(false);

  private oauth = inject(OAuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  async ngOnInit(): Promise<void> {
    const providerId = this.route.snapshot.paramMap.get('provider');
    if (!isOAuthProviderId(providerId)) {
      this.fail('Unknown sign-in provider.');
      return;
    }

    try {
      const params = new URLSearchParams(window.location.search);
      const returnUrl = await this.oauth.completeLogin(providerId, params);
      await this.router.navigateByUrl(returnUrl);
    } catch (e: unknown) {
      this.fail(e instanceof Error ? e.message : 'Sign-in failed.');
    }
  }

  private fail(msg: string): void {
    this.errored.set(true);
    this.message.set(msg);
  }
}
